import { useEffect, useRef, memo, useState, useMemo } from 'react'
import { useLoader } from '@react-three/fiber'
import * as THREE from 'three'
import { FBXLoader } from 'three/addons/loaders/FBXLoader.js'
import { pickTextureUrl } from './textureUtils'
import { useAppStore } from '../../stores/appStore'
import { buildImageUrl, normalizeAssetUrl } from '../../config'
import { resolveAlignCheckerTextureUrl } from '../../utils/alignCheckerTexture'
import { getMaterialTextureRepeatMultiplier } from '../../utils/textileRepeat'
import { findTextureIndexForBedMatSlot, sortMaterialTextures } from '../../utils/textileTextureOrder'
import { ROTATE_QUARTER_TURNS_DEFAULT, SWAP_UV_DEFAULT, rotateTexture, swapMeshUV, clearLegacyMapsOnMesh, unifyRepeatsFromBase, applyMaterialDefaults, applyPBR, loadPBRTextures, calculateUVAwareRepeat } from './textureUtils'

const DEBUG_BED_SLOTS = false
const loggedBedSlotModels = new Set<string>()

/** Bed meshes run swapMeshUV + textile maps use rotateTexture; preserved FBX slots need the same map rotation. */
function rotateMaterialMapsLikeBed(mat: THREE.Material | null, turns: number, swapUv: boolean) {
  if (!mat || !swapUv || !turns) return
  const m = mat as any
  const keys = [
    'map',
    'normalMap',
    'roughnessMap',
    'metalnessMap',
    'aoMap',
    'displacementMap',
    'emissiveMap',
    'lightMap',
    'alphaMap',
    'bumpMap',
  ] as const
  for (const k of keys) {
    if (m[k]) rotateTexture(m[k] as THREE.Texture, turns)
  }
  mat.needsUpdate = true
}

/** Compensate mirrored look for preserved corner-logo materials after UV swap. */
function fixCornerLogoMirroring(mat: THREE.Material | null) {
  if (!mat) return
  const m = mat as any
  const keys = [
    'map',
    'normalMap',
    'roughnessMap',
    'metalnessMap',
    'aoMap',
    'displacementMap',
    'emissiveMap',
    'lightMap',
    'alphaMap',
    'bumpMap',
  ] as const
  for (const k of keys) {
    const tex = m[k] as THREE.Texture | undefined
    if (!tex) continue
    tex.wrapS = THREE.RepeatWrapping
    tex.center.set(0.5, 0.5)
    // Flip horizontally to counter mirrored logo after mesh UV swap.
    tex.repeat.x = -Math.abs(tex.repeat.x || 1)
    tex.offset.x = 1
    tex.needsUpdate = true
  }
  mat.needsUpdate = true
}

function isPreservedFbxSlot(materialNameLower: string): boolean {
  return materialNameLower.includes('jensen_corner_logo')
}

function isDreamMatSlot(materialNameLower: string): boolean {
  return materialNameLower.includes('dreammatslot_1')
}

function isCornerLogoSlot(materialNameLower: string): boolean {
  return materialNameLower.includes('jensen_corner_logo')
}

// Create a shared FBXLoader with LoadingManager that intercepts logo texture paths
// Logo textures are embedded in FBX files but should be loaded from /media/logos/ regardless of FBX location
const bedModelLoader = (() => {
  const manager = new THREE.LoadingManager()

  manager.onError = (url) => {
    console.error('[BedModel] LoadingManager error', { url })
  }

  const originalResolveURL = manager.resolveURL.bind(manager)
  manager.resolveURL = (url: string) => {
    // Normalize all URLs first (HTTP -> HTTPS)
    const normalizedUrl = normalizeAssetUrl(url)
    const stubbed = resolveAlignCheckerTextureUrl(normalizedUrl)
    if (stubbed !== normalizedUrl) {
      return stubbed
    }
    
    // Intercept logo texture requests - these are embedded in FBX files but should be loaded from /media/logos/
    if (/\.(jpg|jpeg|png|gif|bmp|tga)$/i.test(normalizedUrl)) {
      const filename = normalizedUrl.split(/[/\\]/).pop() || normalizedUrl
      // Check if this is a logo texture (Jensen_Logo pattern)
      if (/^Jensen_Logo/i.test(filename)) {
        return buildImageUrl(`/media/logos/${filename}`)
      }
    }
    // Normalize the resolved URL as well
    const resolved = originalResolveURL(normalizedUrl)
    return normalizeAssetUrl(resolved)
  }
  return new FBXLoader(manager)
})()

function BedModelCmp({ 
  modelUrl, 
  meshFilter = null,
  materialOverrides = {},
  textureOverride = null,
  debugMode = false,
  onModelReady,
}: { 
  modelUrl: string
  meshFilter?: ((mesh: THREE.Mesh) => boolean) | null
  materialOverrides?: Record<string, any>
  textureOverride?: any | null
  debugMode?: boolean
  onModelReady?: (fbx: THREE.Object3D) => void
}) {
  const { bedConfig } = useAppStore()
  const textile = bedConfig.textile
  const currentTex = textureOverride || textile?.currentTexture || null
  const textileTextures = textile?.textures || []
  const selectedSize = bedConfig.bed.selectedSize // Width in cm
  const textureResolution = useAppStore((s) => s.textureResolution)
  const ROTATE_QUARTER_TURNS = ROTATE_QUARTER_TURNS_DEFAULT
  const SWAP_UV = SWAP_UV_DEFAULT

  // If textile has multiple textures, prepare maps for each texture
  const hasMultipleTextures = textileTextures.length > 1
  
  // Get stitch_color from textile
  // Note: stitch_color is stored on the Material (textile) object itself, not on individual texture entries
  const stitchColor = textile?.stitch_color || null
  
  // Memoize textileTextures to prevent infinite loops - use a stable reference
  const memoizedTextileTextures = useMemo(() => {
    return sortMaterialTextures(textileTextures)
  }, [textile?.id, JSON.stringify(textileTextures.map((t: any) => ({
    id: t?.texture?.id || t?.id,
    order: t?.order ?? 0,
    slot_name: t?.slot_name,
    slot_label: t?.slot_label,
  })))])

  // State for loaded texture maps (for multiple textures)
  const [allTextureMaps, setAllTextureMaps] = useState<Array<{
    texture: any
    slot_name?: string
    textile_repeat_multiplier?: number
    maps: {
      base: THREE.Texture | null
      normal: THREE.Texture | null
      roughness: THREE.Texture | null
      metalness: THREE.Texture | null
      ao: THREE.Texture | null
      displacement: THREE.Texture | null
    }
  }>>([])

  const [dreamSlotMaps, setDreamSlotMaps] = useState<{
    base: THREE.Texture | null
    normal: THREE.Texture | null
    roughness: THREE.Texture | null
  }>({
    base: null,
    normal: null,
    roughness: null,
  })

  // Load textures for multiple textures case (manually, not using useLoader to avoid hook violations)
  useEffect(() => {
    if (!hasMultipleTextures) {
      setAllTextureMaps([])
      return
    }

    let cancelled = false

    // Clear previous textures immediately when starting a new load to prevent stale textures
    setAllTextureMaps([])

    const loader = new THREE.TextureLoader()
    const loadPromises = memoizedTextileTextures.map(async (textureEntry: any) => {
      // Handle both API structure (textureEntry.texture) and flat structure (textureEntry directly)
      const tex = textureEntry?.texture || textureEntry
      if (!tex) {
        console.warn('[BedModel] Missing texture in entry:', textureEntry)
        return null
      }

      // Only slot_name matches FBX material names (e.g. BedMatSlot_1). Do not fall back to slot_label.
      const slotName = textureEntry?.slot_name || null

      const loadTexture = (url: string | null): Promise<THREE.Texture | null> => {
        if (!url) {
          return Promise.resolve(null)
        }
        return new Promise((resolve) => {
          loader.load(
            url,
            (texture) => {
              texture.flipY = false
              resolve(texture)
            },
            undefined,
            (error) => {
              console.warn('[BedModel] Failed to load texture:', url, error)
              resolve(null) // On error, return null
            }
          )
        })
      }

      const baseUrl = pickTextureUrl((tex as any).base_color_urls, tex?.base_color_url)
      const normalUrl = pickTextureUrl((tex as any).normal_urls, tex?.normal_url)
      const roughnessUrl = pickTextureUrl((tex as any).roughness_urls, tex?.roughness_url)
      const metalnessUrl = pickTextureUrl((tex as any).metalness_urls, tex?.metalness_url)
      const aoUrl = pickTextureUrl((tex as any).ambient_occlusion_urls, tex?.ambient_occlusion_url)
      const displacementUrl = pickTextureUrl((tex as any)?.height_urls, (tex as any)?.height_url)

      const [base, normal, roughness, metalness, ao, displacement] = await Promise.all([
        loadTexture(baseUrl),
        loadTexture(normalUrl),
        loadTexture(roughnessUrl),
        loadTexture(metalnessUrl),
        loadTexture(aoUrl),
        loadTexture(displacementUrl),
      ])

      // Keep array index stable even when optional maps fail to load.
      if (!base) {
        console.warn('[BedModel] No base texture loaded for entry:', textureEntry)
      }

      return {
        texture: tex,
        slot_name: slotName,
        textile_repeat_multiplier: textureEntry?.textile_repeat_multiplier ?? 1.0,
        maps: {
          base,
          normal,
          roughness,
          metalness,
          ao,
          displacement,
        }
      }
    })

    Promise.all(loadPromises).then((results) => {
      if (cancelled) return
      const validResults = results.filter((entry) => entry != null) as any
      setAllTextureMaps(validResults)
    }).catch((error) => {
      if (cancelled) return
      console.error('[BedModel] Error loading multiple textures:', error)
      setAllTextureMaps([])
    })

    return () => {
      cancelled = true
    }
  }, [hasMultipleTextures, textureResolution, memoizedTextileTextures])

  // Dedicated DreamMatSlot_1 maps from backend media.
  useEffect(() => {
    let cancelled = false
    const loader = new THREE.TextureLoader()

    const loadTexture = (url: string): Promise<THREE.Texture | null> =>
      new Promise((resolve) => {
        loader.load(
          url,
          (texture) => {
            texture.flipY = false
            texture.wrapS = THREE.RepeatWrapping
            texture.wrapT = THREE.RepeatWrapping
            resolve(texture)
          },
          undefined,
          () => resolve(null)
        )
      })

    const loadFirstAvailable = async (label: string, candidates: string[]): Promise<THREE.Texture | null> => {
      for (const url of candidates) {
        const tex = await loadTexture(url)
        if (tex) {
          return tex
        }
      }
      console.warn('[DreamMatSlot] Failed to load map', { label, candidates })
      return null
    }

    const run = async () => {
      const [base, normal, roughness] = await Promise.all([
        loadFirstAvailable('base', [
          buildImageUrl('/media/special/DreamMatSlot_BaseColor.png'),
          buildImageUrl('/static/textures/DreamMatSlot_BaseColor.png'),
        ]),
        loadFirstAvailable('normal', [
          buildImageUrl('/media/special/DreamMatSlot_Normal.png'),
          buildImageUrl('/static/textures/DreamMatSlot_Normal.png'),
        ]),
        loadFirstAvailable('roughness', [
          buildImageUrl('/media/special/DreamMatSlot_Roughness.png'),
          buildImageUrl('/static/textures/DreamMatSlot_Roughness.png'),
        ]),
      ])
      if (cancelled) return
      setDreamSlotMaps({ base, normal, roughness })
    }

    run()
    return () => {
      cancelled = true
    }
  }, [])

  // Memoize texture URLs to prevent useLoader from being called with new URLs on every resolution change
  // This ensures useLoader only changes when the actual texture data changes
  const textureUrls = useMemo(() => {
    if (!currentTex) return null
    return {
      base: pickTextureUrl((currentTex as any).base_color_urls, currentTex?.base_color_url),
      normal: pickTextureUrl((currentTex as any).normal_urls, currentTex?.normal_url),
      roughness: pickTextureUrl((currentTex as any).roughness_urls, currentTex?.roughness_url),
      metalness: pickTextureUrl((currentTex as any).metalness_urls, currentTex?.metalness_url),
      ao: pickTextureUrl((currentTex as any).ambient_occlusion_urls, currentTex?.ambient_occlusion_url),
      displacement: pickTextureUrl((currentTex as any)?.height_urls, (currentTex as any)?.height_url),
    }
  }, [currentTex, textureResolution]) // Include textureResolution so URLs update when resolution changes

  // Default maps for single texture or fallback (using useLoader is fine here since it's not in a loop)
  const maps = textureUrls ? {
    base: textureUrls.base ? (useLoader(THREE.TextureLoader, textureUrls.base) as THREE.Texture) : null,
    normal: textureUrls.normal ? (useLoader(THREE.TextureLoader, textureUrls.normal) as THREE.Texture) : null,
    roughness: textureUrls.roughness ? (useLoader(THREE.TextureLoader, textureUrls.roughness) as THREE.Texture) : null,
    metalness: textureUrls.metalness ? (useLoader(THREE.TextureLoader, textureUrls.metalness) as THREE.Texture) : null,
    ao: textureUrls.ao ? (useLoader(THREE.TextureLoader, textureUrls.ao) as THREE.Texture) : null,
    displacement: textureUrls.displacement ? (useLoader(THREE.TextureLoader, textureUrls.displacement) as THREE.Texture) : null,
  } : null

  const fbx = useLoader(bedModelLoader, modelUrl)
  const group = useRef<THREE.Group>(null)
  const onModelReadyCalledRef = useRef<string | null>(null)

  useEffect(() => {
    if (!fbx) return
    fbx.traverse((child) => {
      if (child instanceof THREE.Mesh && child.material) {
        const mats = Array.isArray(child.material) ? child.material : [child.material]
      }
    })
  }, [fbx])

  useEffect(() => {
    if (!fbx) return
    // Scale/position the FBX itself — do NOT gate on group.current.
    // Requiring the ref was a footgun: if the effect ran before the ref
    // attached, onModelReady never fired → __bed_bbox__ never set →
    // __bed_render_ready__ stuck false forever (bed still visible).
    if (onModelReadyCalledRef.current === modelUrl) {
      return
    }

    const box = new THREE.Box3().setFromObject(fbx)
    const size = box.getSize(new THREE.Vector3())

    const BASE_SCALE = 0.05
    fbx.scale.setScalar(BASE_SCALE)

    const center = box.getCenter(new THREE.Vector3())
    fbx.position.set(-center.x * BASE_SCALE, -box.min.y * BASE_SCALE, -center.z * BASE_SCALE)

    const meshInfo: Array<{ name: string; type: string; vertexCount: number; hasUVs: boolean; hasNormals: boolean; materialType: string; materialName: string; hasColor: boolean; color?: string; materialCount: number; materials: Array<{ index: number; type: string; name: string; color?: string }>} > = []

    fbx.traverse((child) => {
      if (child instanceof THREE.Mesh) {
        child.castShadow = true
        child.receiveShadow = true
        // Log any legacy texture URLs still attached to imported materials for diagnosis
        const mats = Array.isArray(child.material) ? child.material : [child.material]
        mats.forEach((m: any) => {
          const urls = [m?.map?.image?.currentSrc, m?.normalMap?.image?.currentSrc, m?.roughnessMap?.image?.currentSrc, m?.metalnessMap?.image?.currentSrc, m?.aoMap?.image?.currentSrc]
          urls.filter(Boolean).forEach((u) => {
            if (typeof u === 'string' && /UV_\d+cm_AlignChecker\.png/i.test(u)) {
              console.warn('[FBX] Legacy UV checker map referenced on', child.name, u)
            }
          })
        })

        const materials = Array.isArray(child.material) ? child.material : [child.material]
        // Store original material names on the mesh for later reference
        // This allows us to detect logo materials even after materials have been replaced
        // Only set if not already set with correct FBX material names (prevent overwriting)
        const existingOriginalNames = (child as any).__originalMaterialNames__
        const hasCorrectExistingNames = existingOriginalNames && existingOriginalNames.some((name: string) => 
          name.includes('BedMatSlot') || name.includes('Jensen_Embroidery_Logo')
        )
        // Check if current materials look like original FBX materials
        const hasOriginalFBXNames = materials.some((mat: any) => {
          const name = mat?.name || ''
          return name.includes('BedMatSlot') || name.includes('Jensen_Embroidery_Logo')
        })
        // Only set if: (1) not already set, OR (2) already set but wrong AND current materials are original FBX materials
        if (!existingOriginalNames || (!hasCorrectExistingNames && hasOriginalFBXNames)) {
          ;(child as any).__originalMaterialNames__ = materials.map((mat: any) => mat?.name || 'unnamed')
        }
        const materialInfo = materials.map((mat, index) => ({ index, type: mat?.type || 'unknown', name: mat?.name || 'unnamed', color: mat?.color ? `#${mat.color.getHexString()}` : undefined }))

        meshInfo.push({
          name: child.name,
          type: child.geometry.type,
          vertexCount: child.geometry.attributes.position?.count || 0,
          hasUVs: !!child.geometry.attributes.uv,
          hasNormals: !!child.geometry.attributes.normal,
          materialType: materials[0]?.type || 'unknown',
          materialName: materials[0]?.name || 'unnamed',
          hasColor: !!materials[0]?.color,
          color: materials[0]?.color ? `#${materials[0].color.getHexString()}` : undefined,
          materialCount: materials.length,
          materials: materialInfo
        })
      }
    })

    // Extract unique material slot names from the model
    const materialSlots = new Set<string>()
    meshInfo.forEach((mesh) => {
      mesh.materials.forEach((mat) => {
        if (mat.name && mat.name !== 'unnamed') {
          materialSlots.add(mat.name)
        }
      })
      // Also add the primary material name
      if (mesh.materialName && mesh.materialName !== 'unnamed') {
        materialSlots.add(mesh.materialName)
      }
    })
    
    // Store material slots globally for access from other components
    const sortedSlots = Array.from(materialSlots).sort()
    ;(window as any).__bed_model_material_slots__ = sortedSlots

    if (!loggedBedSlotModels.has(modelUrl)) {
      loggedBedSlotModels.add(modelUrl)

    }

    if (debugMode) {
      // debug logs removed
      meshInfo.forEach((mesh, index) => {
        // debug: mesh info
        if (mesh.materialCount > 1) {
          // debug: material slots
          mesh.materials.forEach((mat, matIndex) => {
          })
        }
      })
    }

    // Mark processed only after we actually invoke onModelReady successfully.
    // Previously we marked before the call; if Scene early-returned (no
    // currentModelUrl yet), bbox was never set and we never retried.
    if (onModelReady) {
      try {
        onModelReady(fbx)
        onModelReadyCalledRef.current = modelUrl
        console.log('[BedModel] onModelReady called', { modelUrl })
      } catch (err) {
        console.error('[BedModel] onModelReady threw', err)
      }
    } else {
      onModelReadyCalledRef.current = modelUrl
    }
  }, [fbx, onModelReady, modelUrl])

  useEffect(() => {
    if (!fbx || (!currentTex && !hasMultipleTextures)) return
    if (hasMultipleTextures && allTextureMaps.length === 0) {
      return
    }

    ;(window as any).__bed_textile_base_map__ = null

    // Count total materials
    let totalMaterialCount = 0
    fbx.traverse((child) => {
      if (child instanceof THREE.Mesh) {
        if (meshFilter && !meshFilter(child)) return
        const mats = Array.isArray(child.material) ? child.material : [child.material]
        totalMaterialCount += mats.length
      }
    })

    // Apply textures
    let globalMaterialIndex = 0
    fbx.traverse((child) => {
      if (!(child instanceof THREE.Mesh)) return
      
      const sourceMaterials = Array.isArray(child.material) ? child.material : [child.material]
      // clearLegacyMapsOnMesh mutates materials in place (clears maps + resets color).
      // Snapshot preserved slots before that so they keep FBX-authored textures.
      const slotNamesForPreserve: string[] =
        ((child as any).__originalMaterialNames__ as string[] | undefined) ||
        sourceMaterials.map((m: any) => m?.name || '')
      const preservedCornerLogoByIndex = new Map<number, THREE.Material>()
      slotNamesForPreserve.forEach((rawName, idx) => {
        const n = (rawName || '').toLowerCase()
        if (!isPreservedFbxSlot(n)) return
        const src = sourceMaterials[idx] as any
        if (src && typeof src.clone === 'function') {
          preservedCornerLogoByIndex.set(idx, src.clone())
        }
      })
      clearLegacyMapsOnMesh(child)
      swapMeshUV(child, SWAP_UV, 'bed')
      if (meshFilter && !meshFilter(child)) return

      const origMats = sourceMaterials
      const isArray = Array.isArray(origMats)
      const materials = isArray ? origMats : [origMats]
      
      // Get original material names from mesh (stored when FBX was first loaded)
      // This allows us to detect logo materials even after materials have been replaced
      let originalMaterialNames = (child as any).__originalMaterialNames__
      
      // Check if originalMaterialNames exists and contains correct FBX material names
      // If it contains previously created material names (like "Crown_Continental_180x200_Material_6"), it's wrong
      const hasCorrectOriginalNames = originalMaterialNames && originalMaterialNames.some((name: string) => 
        name.includes('BedMatSlot') || name.includes('Jensen_Embroidery_Logo')
      )
      
      if (!originalMaterialNames || !hasCorrectOriginalNames) {
        // Check if current materials look like original FBX materials
        const hasOriginalNames = materials.some((m: any) => {
          const name = m?.name || ''
          return name.includes('BedMatSlot') || name.includes('Jensen_Embroidery_Logo')
        })
        
        if (hasOriginalNames) {
          // These are likely original FBX materials, use them
          originalMaterialNames = materials.map((m: any) => m?.name || 'unnamed')
          ;(child as any).__originalMaterialNames__ = originalMaterialNames
        } else if (!originalMaterialNames) {
          // Fallback: use current material names (may be wrong, but better than nothing)
          // Only do this if originalMaterialNames doesn't exist at all
          originalMaterialNames = materials.map((m: any) => m?.name || 'unnamed')
        }
        // If originalMaterialNames exists but is wrong, don't overwrite it - keep the wrong values
        // This prevents infinite loops, but means logo detection will fail
      }

      const newMaterials = materials.map((origMat: any, index: number) => {
        globalMaterialIndex++ // Still track global for counting, but use local index for texture selection
        
        const mat = new THREE.MeshStandardMaterial()
        mat.color = new THREE.Color(0xffffff)
        ;(mat as any).emissive?.setRGB?.(0, 0, 0)
        ;(mat as any).vertexColors = false

        if (materialOverrides[mat.name]) {
          Object.assign(mat, materialOverrides[mat.name])
          mat.needsUpdate = true
          return mat
        }

        // Apply textures to BedMatSlot_* materials by NAME, not by index
        // This is necessary because material order differs between bed sizes
        // (e.g., bed size 120 has BedMatSlot_2 first, then DreamMatSlot_1, then BedMatSlot_1)
        let materialMaps: any = null
        let mapsEntry: ((typeof allTextureMaps)[number] | null) = null
        let mappedTextureIndex = -1

        const originalMatName = originalMaterialNames[index] || origMat?.name || ''
        const materialName = (originalMatName || origMat?.name || '').toLowerCase()
        const isDreamSlot = isDreamMatSlot(materialName)
        const isCornerLogo = isCornerLogoSlot(materialName)

        // DreamMatSlot_1 always gets a fully fresh material (never preserved from FBX),
        // so no FBX maps/properties can bleed through.
        if (isDreamSlot) {
          const dreamMat = new THREE.MeshStandardMaterial()
          dreamMat.color = new THREE.Color(0xffffff)
          ;(dreamMat as any).emissive?.setRGB?.(0, 0, 0)
          ;(dreamMat as any).vertexColors = false
          // Force fully opaque, non-shiny textile-like response and block FBX bleed-through.
          dreamMat.transparent = false
          dreamMat.opacity = 1.0
          dreamMat.alphaMap = null
          dreamMat.emissiveMap = null
          dreamMat.lightMap = null
          dreamMat.aoMap = null
          dreamMat.metalnessMap = null
          dreamMat.displacementMap = null
          dreamMat.bumpMap = null
          // Reduce "chalky/faint" look: less roughness and no environment haze.
          dreamMat.roughness = 0.72
          dreamMat.metalness = 0.0
          dreamMat.envMapIntensity = 0.0
          // Slight tint-down helps preserve print contrast under strong scene lighting.
          dreamMat.color = new THREE.Color(0xe6e6e6)
          dreamMat.depthTest = true
          dreamMat.depthWrite = true
          dreamMat.side = THREE.FrontSide

          if (dreamSlotMaps.base) {
            const base = dreamSlotMaps.base.clone()
            // @ts-ignore
            if ((THREE as any).SRGBColorSpace) {
              // @ts-ignore
              base.colorSpace = (THREE as any).SRGBColorSpace
            } else if ((THREE as any).sRGBEncoding) {
              // @ts-ignore
              base.encoding = (THREE as any).sRGBEncoding
            }
            dreamMat.map = base
          }
          if (dreamSlotMaps.normal) {
            const normal = dreamSlotMaps.normal.clone()
            ;(normal as any).colorSpace = (THREE as any).LinearSRGBColorSpace ?? (THREE as any).LinearEncoding
            dreamMat.normalMap = normal
            dreamMat.normalScale = new THREE.Vector2(2.0, -2.0)
          }
          if (dreamSlotMaps.roughness) {
            const roughness = dreamSlotMaps.roughness.clone()
            ;(roughness as any).colorSpace = (THREE as any).LinearSRGBColorSpace ?? (THREE as any).LinearEncoding
            dreamMat.roughnessMap = roughness
          }
          rotateMaterialMapsLikeBed(dreamMat, ROTATE_QUARTER_TURNS, SWAP_UV)
          unifyRepeatsFromBase(dreamMat)
          dreamMat.needsUpdate = true
          return dreamMat
        }

        // Preserve selected FBX slots exactly as authored in source.
        if (isPreservedFbxSlot(materialName)) {
          const snap = preservedCornerLogoByIndex.get(index)
          if (snap) {
            rotateMaterialMapsLikeBed(snap, ROTATE_QUARTER_TURNS, SWAP_UV)
            if (isCornerLogo) {
              fixCornerLogoMirroring(snap)
            }
            snap.needsUpdate = true
            return snap
          }
          const srcMat: any = origMats[index]
          const preserved = srcMat && typeof srcMat.clone === 'function'
            ? srcMat.clone()
            : new THREE.MeshStandardMaterial().copy(srcMat || new THREE.MeshStandardMaterial())
          rotateMaterialMapsLikeBed(preserved, ROTATE_QUARTER_TURNS, SWAP_UV)
          if (isCornerLogo) {
            fixCornerLogoMirroring(preserved)
          }
          preserved.needsUpdate = true
          return preserved
        }
        const isStitchMaterial =
          materialName.includes('logo') ||
          materialName.includes('stitch') ||
          materialName.includes('embroidery') ||
          materialName.includes('darkseam') ||
          materialName.includes('lightseam')
        const isBedSlot = originalMatName.startsWith('BedMatSlot_')

        // Debug mode: color-code textile-related slots to see what part of the bed they control
        if (DEBUG_BED_SLOTS && (isBedSlot || isStitchMaterial)) {
          // Separate palettes so bed vs stitch slots are easy to distinguish
          const bedSlotColors: number[] = [0xff0000, 0x00ff00, 0x0000ff]
          const stitchSlotColors: number[] = [0xffff00, 0xff00ff, 0x00ffff]

          const palette = isBedSlot ? bedSlotColors : stitchSlotColors
          const debugColor = palette[index % palette.length] as number

          mat.color = new THREE.Color(debugColor)
          mat.map = null
          mat.normalMap = null
          mat.roughnessMap = null
          mat.metalnessMap = null
          ;(mat as any).aoMap = null
          mat.displacementMap = null
          mat.roughness = 1.0
          mat.metalness = 0
          mat.needsUpdate = true

          console.log(isBedSlot ? '[BedSlotDebug] Slot material' : '[StitchSlotDebug] Slot material', {
            meshName: child.name,
            materialIndex: index,
            originalMatName,
            isBedSlot,
            isStitchMaterial,
            debugColor: `#${debugColor.toString(16).padStart(6, '0')}`,
          })

          return mat
        }

        if (hasMultipleTextures && allTextureMaps.length > 0) {
          const originalMatNameForIndex = originalMaterialNames[index] || origMat?.name || ''
          const originalMatNameLower = (originalMatNameForIndex || '').toLowerCase()

          // Prefer explicit name-based mapping when textile slot_names are provided
          const hasNamedTextureSlots = allTextureMaps.length > 0
            && allTextureMaps.every((m) => !!(m.slot_name || '').trim())
          let textureIndex = -1
          let bedSlotIndices: number[] = []
          let bedSlotNamesInOrder: string[] = []

          if (hasNamedTextureSlots && originalMatNameForIndex) {
            // Never apply textiles to BedMatSlot_3
            if (originalMatNameLower !== 'bedmatslot_3') {
              // Try to find a texture whose slot_name matches this material slot name
              const namedIndex = allTextureMaps.findIndex((m) => {
                const slot = (m.slot_name || '').toLowerCase()
                return slot === originalMatNameLower
              })
              if (namedIndex >= 0) {
                mappedTextureIndex = namedIndex
                mapsEntry = allTextureMaps[mappedTextureIndex] || null
                textureIndex = namedIndex
              }
            }
          }

          // Fallback: original index-based mapping ONLY when no slot_names exist.
          // If we have named texture slots but no name match for this material,
          // we intentionally leave it without a multi-textile applied.
          if (!mapsEntry && !hasNamedTextureSlots) {
            // Find BedMatSlot_* materials (exclude DreamMatSlot_* and logo materials)
            // and order them by numeric suffix so mapping is stable across FBX variants.
            const bedSlots: Array<{ idx: number; name: string; order: number }> = []
            const originalNamesArray: string[] = Array.isArray(originalMaterialNames)
              ? (originalMaterialNames as string[])
              : []
            originalNamesArray.forEach((rawName: string, idx: number) => {
              const name = rawName || ''
              if (name.startsWith('BedMatSlot_') && !name.includes('Dream') && !name.includes('Logo') && name.toLowerCase() !== 'bedmatslot_3') {
                const match = name.match(/BedMatSlot_(\d+)/)
                const order = match ? parseInt(match[1] || '0', 10) : Number.MAX_SAFE_INTEGER
                bedSlots.push({ idx, name, order })
              }
            })
            bedSlots.sort((a, b) => a.order - b.order)
            bedSlotIndices = bedSlots.map((s) => s.idx)
            bedSlotNamesInOrder = bedSlots.map((s) => s.name)
            
            // Assign textures to BedMatSlot_N using slot_name or stable slot number.
            textureIndex = bedSlotIndices.indexOf(index)
            if (textureIndex >= 0) {
              const bedSlotNumber = bedSlots[textureIndex]?.order
              if (bedSlotNumber != null) {
                mappedTextureIndex = findTextureIndexForBedMatSlot(bedSlotNumber, allTextureMaps)
                mapsEntry = mappedTextureIndex >= 0 ? allTextureMaps[mappedTextureIndex] || null : null
              }
            }
          }

          if (mapsEntry) {
            const selectedMaps = mapsEntry.maps

            // Debug which texture slot_name is being applied to which BedMatSlot
            console.log('[MultiTextileDebug] Applying multi-textile to bed slot', {
              meshName: child.name,
              materialIndex: index,
              originalMaterialName: originalMatNameForIndex,
              textureIndex,
              mappedTextureIndex,
              textureSlotName: mapsEntry.slot_name || null,
              availableTextureSlotNames: allTextureMaps.map((m) => m.slot_name || null),
              bedSlotIndices,
              bedSlotNamesInOrder,
              usedNameBasedMapping: hasNamedTextureSlots && !!mapsEntry.slot_name,
            })

            if (selectedMaps?.base) {
              materialMaps = {
                base: selectedMaps.base.clone(),
                normal: selectedMaps.normal?.clone() || null,
                roughness: selectedMaps.roughness?.clone() || null,
                metalness: selectedMaps.metalness?.clone() || null,
                ao: selectedMaps.ao?.clone() || null,
                displacement: selectedMaps.displacement?.clone() || null,
              }
            }
          }
          // Nothing for other slots without a mapping
        } else if (!hasMultipleTextures && maps && materialName !== 'bedmatslot_3') {
          materialMaps = maps
        }

        // Early return for non-stitch materials without texture maps
        // Stitch/logo materials continue even without materialMaps to get stitch color applied
        if (!materialMaps && !isStitchMaterial) return mat

        // For stitch/logo materials without materialMaps, skip texture application but continue to stitch handling
        // These use embedded textures from FBX, so we don't apply textile textures
        if (isStitchMaterial && !materialMaps) {
        } else if (materialMaps) {
          // Calculate repeat (only for materials with texture maps)
          const bedBox = (window as any).__bed_bbox__
          let desiredRepeatX = selectedSize ? selectedSize / 100 : 1
          let desiredRepeatY = 2
          const BASE_SCALE = 0.05
          let bedLengthCm = null
          let bedWidthCm = selectedSize || null
          
          if (bedBox && !bedBox.isEmpty()) {
            bedLengthCm = (bedBox.max.z - bedBox.min.z) / BASE_SCALE
            bedWidthCm = (bedBox.max.x - bedBox.min.x) / BASE_SCALE
            desiredRepeatY = bedLengthCm / 100
          }
          
          const multiplier =
            mapsEntry?.textile_repeat_multiplier ??
            getMaterialTextureRepeatMultiplier(bedConfig.textile?.textures, {
              index: mappedTextureIndex >= 0 ? mappedTextureIndex : undefined,
              textureId: bedConfig.textile?.currentTexture?.id,
            })
          const desiredRepeatXBeforeMultiplier = desiredRepeatX
          const desiredRepeatYBeforeMultiplier = desiredRepeatY
          desiredRepeatX *= multiplier
          desiredRepeatY *= multiplier
          const repeat = calculateUVAwareRepeat(child, desiredRepeatX, desiredRepeatY, true, true) // Enable debug logging and normalize UV span
          ;(window as any).__bed_textile_desired_repeat__ = { x: desiredRepeatX, y: desiredRepeatY }
          ;(window as any).__bed_textile_final_repeat__ = { x: repeat.x, y: repeat.y } // Store final repeat after normalization
          
          // Debug logging for texture scaling - expanded for easier comparison
          const debugInfo = {
            meshName: child.name,
            bedDimensions: {
              widthCm: bedWidthCm,
              lengthCm: bedLengthCm,
              selectedSizeCm: selectedSize
            },
            desiredRepeat: {
              x: desiredRepeatXBeforeMultiplier,
              y: desiredRepeatYBeforeMultiplier
            },
            multiplier: multiplier,
            desiredRepeatAfterMultiplier: {
              x: desiredRepeatX,
              y: desiredRepeatY
            },
            finalRepeat: {
              x: repeat.x,
              y: repeat.y
            },
            textileId: bedConfig.textile?.id,
            textileName: bedConfig.textile?.name
          }

          // Apply PBR
          applyPBR({
            mat,
            origMat,
            type: 'textile',
            meshName: child.name,
            maps: materialMaps,
            rotateTurns: ROTATE_QUARTER_TURNS,
            hasUV2: !!(child.geometry as any).attributes.uv2,
            repeat,
            offset: { x: 0, y: 0 },
            rotation: 0,
          })

          // Majestic topper reuses this map (already loaded for the bed) — no second download.
          const slotLower = originalMatName.toLowerCase()
          if (
            materialMaps.base &&
            (slotLower === 'bedmatslot_1' ||
              (!hasMultipleTextures &&
                slotLower.startsWith('bedmatslot_') &&
                slotLower !== 'bedmatslot_3'))
          ) {
            ;(window as any).__bed_textile_base_map__ = materialMaps.base
          }
        }

        // Special handling for stitch/logo material slots - adjust roughnessMap, normalMap, and apply stitch color
        if (isStitchMaterial) {
          
          // Apply stitch color if available (overrides texture base color)
          if (stitchColor && !isCornerLogo) {
            const stitchColorObj = new THREE.Color(stitchColor)
            
            // Get maps from the material (which may have been set by applyPBR or from origMat)
            // For logo materials without materialMaps, get maps from original material
            const normalMap = mat.normalMap || (origMat?.normalMap || null)
            const roughnessMap = mat.roughnessMap || (origMat?.roughnessMap || null)
            const displacementMap = mat.displacementMap || (origMat?.displacementMap || null)
            
            // Create a new material with the stitch color
            const logoMat = new THREE.MeshStandardMaterial({
              color: stitchColorObj,
              normalMap: normalMap,
              roughnessMap: roughnessMap,
              displacementMap: displacementMap,
              roughness: 1.0, // Maximum roughness for matte appearance
              metalness: 0,
              emissive: new THREE.Color(0, 0, 0),
              side: mat.side || origMat?.side || THREE.FrontSide,
              transparent: false,
              opacity: 1.0,
              depthWrite: true,
              depthTest: true,
            })
            
            // Ensure maps are properly configured if they exist
            if (logoMat.roughnessMap) {
              logoMat.roughnessMap.flipY = false
            }
            if (logoMat.displacementMap) {
              logoMat.displacementMap.flipY = false
              logoMat.displacementScale = 0.001
              logoMat.displacementBias = 0
            }
            
            // Set normal scale if normal map exists (increase intensity for more pronounced detail)
            if (logoMat.normalMap) {
              logoMat.normalScale = new THREE.Vector2(2.0, 2.0)
            }
            
            logoMat.name = `${child.name}_Material_${index}`
            logoMat.needsUpdate = true
            
            // Return the logo material instead of the regular material
            // This ensures it becomes part of newMaterials array and gets applied
            return logoMat
          } else {
            // No stitch color - adjust normalMap intensity/scale for more pronounced detail
            if (mat.normalMap) {
              mat.normalScale = new THREE.Vector2(2.0, 2.0)
              mat.normalMap.needsUpdate = true
            }
            
            // Adjust roughnessMap - increase roughness for more matte appearance
            if (mat.roughnessMap) {
              mat.roughness = 1.0
              mat.roughnessMap.needsUpdate = true
            } else {
              mat.roughness = 1.0
            }
            
            mat.needsUpdate = true
          }
        }

        mat.transparent = false
        mat.opacity = 1.0
        mat.depthTest = true
        mat.depthWrite = true
        mat.name = `${child.name}_Material_${index}`
        mat.needsUpdate = true
        
        return mat
      })
      
      child.material = isArray ? newMaterials : newMaterials[0]
      
      if (!hasMultipleTextures) {
        const matsToUnify = Array.isArray(child.material) ? child.material : [child.material]
        matsToUnify.forEach(unifyRepeatsFromBase)
      }
    })

    if (typeof window !== 'undefined') {
      window.dispatchEvent(new CustomEvent('bed-textile-applied'))
    }
  }, [fbx, maps, currentTex, hasMultipleTextures, allTextureMaps, selectedSize, bedConfig.textile?.textures, stitchColor, meshFilter, materialOverrides])

  if (!fbx) return null as any

  return (
    <group ref={group}>
      <primitive object={fbx} />
    </group>
  )
}

const BedModel = memo(BedModelCmp)
export default BedModel


